'use client'

import { useIsLoggedInQuery } from 'auth/hooks'
import * as React from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import { Checkbox } from 'primitives/cf-checkbox'
import type { GetHomePageRecommendationsResponse } from 'cf-grpc/creativefabrica/content/v2/service_pb'
import Image from 'next/image'
import { Link } from '@repo/web-ui/components/link'
import { useInView } from 'framer-motion'
import { Divider } from 'components/divider'
import {
  useUserProfileFirstNameQuery,
  useUserProfileContentInterestQuery,
} from '../_hooks/use-user-profile-info'
import {
  useScrollRestorationState,
  useScrollRestorationEffects,
} from '../_hooks/use-scroll-restoration'
import {
  useInfiniteHomepageProductsQuery,
  useHomepageFirstPageProductsQuery,
  FONTS_CATEGORY,
  GRAPHICS_CATEGORY,
  CRAFTS_CATEGORY,
  EMBROIDERY_CATEGORY,
  BUNDLES_CATEGORY,
} from '@web/hooks/use-paginated-homepage-products'
import { usePrefetchLegacyFavoritesCounts } from '@web/hooks/use-content-favorite'
import type { Category } from '@web/hooks/use-paginated-homepage-products'
import { SpinnerFasIcon } from '@web/components/icons/spinner-fas'
import { LoadMoreTrigger } from '@web/components/algolia/components/content-search-results/list-products'
import type { Language } from 'codegen/types'
import { Button } from '@repo/design-system-cf/components/button'
import { Separator } from 'primitives/separator'
import { rootPath } from '@repo/web-utils/root-path'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { InlineStaticVariant } from '@repo/web-ui/components/static-variants-renderer/inline/inline-static-variant'
import { LOGGED_IN } from '@repo/web-ui/components/static-variants-renderer/utils/conditions'
import { keepPreviousData } from '@tanstack/react-query'
import { ProductCard } from '@web/components/product-card/product-card'

interface Filters {
  label: string
  value: string
  filterCategory: Category
}

const filters: Filters[] = [
  { label: 'filters.fonts', value: 'fonts', filterCategory: FONTS_CATEGORY },
  { label: 'filters.graphics', value: 'graphics', filterCategory: GRAPHICS_CATEGORY },
  { label: 'filters.crafts', value: 'crafts', filterCategory: CRAFTS_CATEGORY },
  { label: 'filters.embroidery', value: 'embroidery', filterCategory: EMBROIDERY_CATEGORY },
  { label: 'filters.bundles', value: 'bundles', filterCategory: BUNDLES_CATEGORY },
]

const filterValues = filters.map(filter => filter.value)

// oxlint-disable-next-line complexity
export function HomeProducts({
  initialProductData,
  mobileMinColumns = 1,
}: {
  initialProductData: GetHomePageRecommendationsResponse | null
  mobileMinColumns?: number
}) {
  const t = useTranslations('pages.home')
  const locale = useLocale()

  const { data: userFirstName } = useUserProfileFirstNameQuery()
  const { data: userContentInterests, isPending: userContentInterestsLoading } =
    useUserProfileContentInterestQuery()

  const { data: isLoggedIn } = useIsLoggedInQuery()

  const [selectedInterest, setSelectedInterest] = React.useState<string[]>(filterValues)
  const [filtersApplied, setFiltersApplied] = React.useState<boolean>(false)

  const { scrollRestoration, setScrollRestoration, SCROLL_STORAGE_KEY, PRODUCTS_STORAGE_KEY } =
    useScrollRestorationState()

  const filterCategories = filters
    .map(filter => {
      // oxlint-disable-next-line array-callback-return
      if (selectedInterest?.includes(filter.value)) {
        return filter.filterCategory
      }
    })
    .filter(Boolean)

  //When user is logged in, we want to fetch personalized products
  const shouldFetchFirstPageProducts =
    selectedInterest.length < filterValues.length || filtersApplied

  const { data: firstPageProductData, isFetching: firstPageProductDataFetching } =
    useHomepageFirstPageProductsQuery(
      {
        categories: filterCategories as number[],
        locale: locale as Language,
      },
      {
        enabled: shouldFetchFirstPageProducts,
        placeholderData: keepPreviousData,
        retry: 1,
      },
    )

  const canFetchNextPageRef = React.useRef<HTMLDivElement>(null)
  const canFetchNextPage = useInView(canFetchNextPageRef, { once: true })

  const {
    data: infiniteProductData,
    hasNextPage,
    fetchNextPage,
    isPending: infiniteProductsAreLoading,
    isFetching: infiniteProductsAreFetching,
  } = useInfiniteHomepageProductsQuery(
    {
      categories: filterCategories as number[],
      locale: locale as Language,
    },
    {
      enabled:
        !userContentInterestsLoading &&
        !firstPageProductDataFetching &&
        (canFetchNextPage || scrollRestoration !== null),
      placeholderData: keepPreviousData,
      retry: 1,
    },
  )

  let products = firstPageProductData?.products
    ? firstPageProductData?.products || []
    : initialProductData?.products || []
  if (
    (canFetchNextPage || scrollRestoration !== null) &&
    infiniteProductData &&
    infiniteProductData?.pages
  ) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    products = [...infiniteProductData?.pages.flatMap(page => page.products)]
  }

  usePrefetchLegacyFavoritesCounts(products.map(p => p.id))

  const handleSelectedInterest = React.useCallback(
    (val: string) => {
      setFiltersApplied(true)
      if (selectedInterest?.includes(val)) {
        if (selectedInterest.length === 1) {
          return
        }
        setSelectedInterest(selectedInterest.filter(interest => interest !== val))
      } else {
        setSelectedInterest(selectedInterest ? [...selectedInterest, val] : [val])
      }
    },
    [selectedInterest],
  )

  React.useEffect(() => {
    if (!userContentInterestsLoading && userContentInterests && userContentInterests.length > 0) {
      const filteredInterests = userContentInterests
        .filter(interest => filterValues.includes(interest.toLocaleLowerCase()))
        .map(filter => filter.toLocaleLowerCase())
      setSelectedInterest(filteredInterests.length > 0 ? filteredInterests : filterValues)
      return
    }
  }, [userContentInterestsLoading, userContentInterests])

  const { productOnClick } = useScrollRestorationEffects({
    scrollRestoration,
    setScrollRestoration,
    SCROLL_STORAGE_KEY,
    PRODUCTS_STORAGE_KEY,
    productsLength: products.length,
    hasNextPage: !!hasNextPage,
    isFetching: infiniteProductsAreFetching,
    fetchNextPage,
    filtersApplied,
  })

  return (
    <div className="flex w-full flex-col items-center justify-center bg-[#F3F5FA] px-[10px]">
      <div
        suppressHydrationWarning
        className={tw('flex w-full flex-col px-[10px] pb-5', 'max-w-[1800px]')}
      >
        <Divider className="bg-[#e3e1e9]" />

        <div className="flex flex-col pt-[15px]">
          <div className="flex flex-col items-center gap-[10px] lg:flex-row lg:justify-between">
            <h3
              className={tw(
                isLoggedIn && userFirstName
                  ? 'text-gray-purple-300'
                  : 'whitespace-nowrap font-bold',
                'text-20',
              )}
            >
              <InlineStaticVariant
                conditions={[LOGGED_IN]}
                getVariant={({ conditionsMet }) =>
                  conditionsMet ? (
                    <LoggedInProductsTitle userFirstName={userFirstName} />
                  ) : (
                    <LoggedOutProductsTitle />
                  )
                }
              />
            </h3>
            <div className="hidden flex-wrap justify-center gap-3 pb-3 md:flex">
              {filters.map(filter => {
                return (
                  <Checkbox
                    key={filter.value}
                    label={t(filter.label)}
                    value={filter.value}
                    checked={selectedInterest ? selectedInterest.includes(filter.value) : false}
                    className="text-aubergine-500"
                    labelClassname={tw(
                      selectedInterest && selectedInterest.includes(filter.value)
                        ? 'text-aubergine-500'
                        : 'text-gray-purple-300',
                      'text-14 cursor-pointer',
                    )}
                    checkboxClassname="mt-[3px]"
                    onClick={() => {
                      handleSelectedInterest(filter.value)
                    }}
                  />
                )
              })}
            </div>
          </div>

          <div
            suppressHydrationWarning
            className={tw(
              'grid grid-cols-[minmax(0,_300px)] items-center justify-center gap-5 pt-5 min-[576px]:grid-cols-[repeat(2,_minmax(0,_300px))] min-[992px]:grid-cols-[repeat(3,_minmax(0,_300px))]',
              'min-[1200px]:grid-cols-[repeat(4,_minmax(0,_300px))] min-[1500px]:grid-cols-[repeat(5,_minmax(0,_300px))] min-[1800px]:grid-cols-[repeat(6,_minmax(0,_300px))]',
              mobileMinColumns === 2 ? 'grid-cols-[repeat(2,_minmax(0,_300px))]' : '',
              mobileMinColumns === 3
                ? 'grid-cols-[repeat(3,_minmax(0,_1fr))] min-[576px]:grid-cols-[repeat(3,_minmax(0,_300px))] gap-2 min-[992px]:gap-5'
                : '',
            )}
          >
            {products.map((product, index) => {
              return (
                <div key={product.id} className="flex h-full w-full items-center justify-center">
                  <ProductCard
                    productId={String(product.id)}
                    productUrl={`/product/${product.slug}/`}
                    image={product.thumbnailUrl || ''}
                    name={product.title}
                    category={product.category?.name || ''}
                    subcategory={product.category?.children[0]?.slug || product.category?.slug}
                    onClick={() => productOnClick(String(product.id), index)}
                    imageClassName="aspect-[3/2] object-cover"
                    isLegacy
                  />
                </div>
              )
            })}
          </div>

          <div ref={canFetchNextPageRef} />

          {infiniteProductsAreLoading || infiniteProductsAreFetching ? (
            <div className="text-gray-purple-300 mt-5 h-full w-full text-center">
              <SpinnerFasIcon className="mr-3 inline size-8 animate-spin" />
              <span className="text-20">{t('products.loading')}</span>
            </div>
          ) : (
            <LoadMoreTrigger
              showMore={fetchNextPage}
              canLoadMore={products.length > 0 ? !!hasNextPage : false}
            />
          )}

          {products.length > 0 &&
          !hasNextPage &&
          !infiniteProductsAreLoading &&
          !infiniteProductsAreFetching ? (
            <NoMoreProducts />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function NoMoreProducts() {
  const t = useTranslations('pages.home.products.no_more_products')
  return (
    <div className="grid grid-cols-[1fr,1px,1fr] px-[10px] pt-5 text-[#2a1f3f]">
      <div className="my-10 flex flex-col items-center">
        <Image
          src={`${rootPath}/wp-content/themes/creativefabricanew/assets/images/ic-feature-earn.png`}
          width={77}
          height={80}
          alt={t('1.title')}
          className="m-6"
        />
        <h4 className="text-20 my-[10px] font-medium">{t('1.title')}</h4>
        <p className="text-14 text-gray-purple-300 mb-[10px]">{t('1.subtitle')}</p>
        <Button variant="primary-green" className="mt-1 uppercase" asChild>
          <Link href={AppRoutes.AFFILIATES}>{t('1.cta')}</Link>
        </Button>
      </div>
      <Separator orientation="vertical" className="h-[240px] self-center" />
      <div className="my-10 flex flex-col items-center">
        <Image
          src={`${rootPath}/wp-content/themes/creativefabricanew/assets/images/ic-feature-sell.png`}
          width={77}
          height={80}
          alt={t('2.title')}
          className="m-6"
        />
        <h4 className="text-20 my-[10px] font-medium">{t('2.title')}</h4>
        <p className="text-14 text-gray-purple-300 mb-[10px]">{t('2.subtitle')}</p>
        <Button variant="primary-green" className="mt-1 uppercase" asChild>
          <Link href={AppRoutes.OPEN_STORE}>{t('2.cta')}</Link>
        </Button>
      </div>
    </div>
  )
}

const LoggedOutProductsTitle = () => {
  const t = useTranslations('pages.home')
  return t('products.logged_out')
}

const LoggedInProductsTitle = ({ userFirstName }: { userFirstName: string | null | undefined }) => {
  const t = useTranslations('pages.home')

  if (!userFirstName) {
    return t('products.without_name')
  }

  return t.rich('products.with_name', {
    userName: userFirstName,
    b: chunks => <span className="text-aubergine-500 whitespace-nowrap font-bold">{chunks}</span>,
  })
}
